

import BaseService from './BaseService';
import { buildCatalogCacheKey, getCatalogCache, setCatalogCache } from '../Utils/CatalogCache';

export interface InfoProps {
  store_id: number | string;
}

export interface ProductsProps {
  store_id: number;
  order_type: number;
  catalog_type?: string;
}

export interface ProductsOptions {
  /**
   * Ativa o uso do cache local (localStorage) com TTL. Default: true.
   * Passe false para forçar sempre uma busca na rede (ex.: pull-to-refresh).
   */
  useCache?: boolean;
  /**
   * Chamado quando o catálogo é revalidado em background (cache vencido).
   * Recebe o catálogo atualizado vindo da rede para a UI substituir o "stale".
   */
  onRefresh?: (freshData: any) => void;
}

export interface TablesProps {
  store_id: number;
  tables: boolean;
  table_categories?: any;
}

export interface FeaturedProductsProps {
  store_id: number;
  order_type: number;
  catalog_type?: string;
}

export interface BannersProps {
  store_id: number;
  order_type: number;
}

export interface ListPaymentMethodsProps {
  store_id: number;
  order_type: number;
}

export interface DistanceProps {
  store_id: number;
  latitude: number;
  longitude: number;
  success?: boolean;
  message?: string;
}

export interface StoreListProps {
  state?: string;
  city?: string;
  neighborhood?: string;
  latitude?: number;
  longitude?: number;
}

// export interface PIXPROPS {
//   payment_method_id: number;
//   payment_method: number;
//   store_id: number;
//   orderType: number;
//   cashier_id: number;
//   store_cashiers?: any;
// }
export interface CashiersProps {
  store_id: number;
  user: boolean;
  sort: string;
  date_from: string;
  date_to: string;
  store_cashiers?: any;
}

export default class StoreService {
  baseService: BaseService;
  ApiBaseService: BaseService;
  constructor(orderType: string) {
    this.baseService = new BaseService(orderType);
    this.ApiBaseService = new BaseService('');
  }

  async info (params: InfoProps): Promise<InfoProps> {
    try {
      const store_id = params.store_id

      const response = await this.ApiBaseService.http.get<InfoProps>(`stores/${store_id}/info`);
      return response.data;
    } catch (error) {
      console.error('Erro info:', error);
      throw error;
    }
  }

  //* Busca o catálogo direto na rede (sem cache).
  private async fetchProductsFromNetwork (params: ProductsProps): Promise<any> {
    const store_id = params.store_id
    const order_type = params.order_type
    const catalog_type = params.catalog_type

    const queryParams = new URLSearchParams({
      store_id: String(store_id),
      order_type: String(order_type)
    });

    if (catalog_type) {
      queryParams.append('catalog_type', catalog_type);
    }

    const response = await this.baseService.http.get<ProductsProps>(`stores/products?${queryParams.toString()}`);
    return response.data;
  }

  async products (params: ProductsProps, options: ProductsOptions = {}): Promise<any> {
    const { useCache = true, onRefresh } = options;

    //* Sem cache: comportamento original (sempre busca na rede).
    if (!useCache) {
      try {
        return await this.fetchProductsFromNetwork(params);
      } catch (error) {
        console.error('Erro products:', error);
        throw error;
      }
    }

    const cacheKey = buildCatalogCacheKey({
      store_id: params.store_id,
      order_type: params.order_type,
      catalog_type: params.catalog_type,
    });

    const cached = getCatalogCache(cacheKey);

    //* Cache válido (dentro do TTL): retorna imediatamente, sem tocar na rede.
    if (cached && !cached.isExpired) {
      return cached.data;
    }

    //* Cache vencido: retorna o dado antigo (stale) na hora e revalida em
    //* background, atualizando o cache e notificando a UI via onRefresh.
    if (cached && cached.isExpired) {
      this.fetchProductsFromNetwork(params)
        .then((freshData) => {
          if (Array.isArray(freshData) && freshData.length > 0) {
            setCatalogCache(cacheKey, freshData);
            if (onRefresh) onRefresh(freshData);
          }
        })
        .catch((error) => {
          console.error('Erro ao revalidar catálogo em background:', error);
        });

      return cached.data;
    }

    //* Sem cache (primeiro acesso): busca na rede e salva no cache.
    try {
      const freshData = await this.fetchProductsFromNetwork(params);
      setCatalogCache(cacheKey, freshData);
      return freshData;
    } catch (error) {
      console.error('Erro products:', error);
      throw error;
    }
  }

  async tables (params: TablesProps): Promise<TablesProps> {
    try {
      const payload = { store_id: params.store_id, tables: params.tables };
      const response = await this.baseService.http.post<TablesProps>('tables/list', payload);
      return response.data.table_categories;
    } catch (error) {
      console.error('Erro products:', error);
      throw error;
    }
  }

  async featuredProducts (params: FeaturedProductsProps): Promise<FeaturedProductsProps> {
    try {
      const store_id = params.store_id
      const order_type = params.order_type
      const catalog_type = params.catalog_type

      const queryParams = new URLSearchParams({
        store_id: String(store_id),
        order_type: String(order_type)
      });

      if (catalog_type) {
        queryParams.append('catalog_type', catalog_type);
      }

      const response = await this.baseService.http.get<FeaturedProductsProps>(`stores/featureds?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Erro featured products:', error);
      throw error;
    }
  }

  async listPaymentMethods (params: ListPaymentMethodsProps): Promise<ListPaymentMethodsProps> {
    try {
      const response = await this.ApiBaseService.http.get<ListPaymentMethodsProps>(`stores/${params.store_id}/payment/list?order_type=${params.order_type}`);
      return response.data;
    } catch (error) {
      console.error('Erro listPayment:', error);
      throw error;
    }
  }

  async cashiers (payload: CashiersProps): Promise<CashiersProps> {
    try {
      const response = await this.baseService.http.post<CashiersProps>('cashiers/list', payload);
      return response.data.store_cashiers;
    } catch (error) {
      console.error('Erro cachiers:', error);
      throw error;
    }
  }

  async distance (params: DistanceProps): Promise<DistanceProps> {
    try {
      const response = await this.baseService.http.get<DistanceProps>(`stores/${params.store_id}/distance?lat=${params.latitude}&lng=${params.longitude}`);
      return response.data;
    } catch (error) {
      console.error('Erro distance:', error);
      throw error;
    }
  }

  async stores(params: StoreListProps): Promise<any> {
    try {
      const mapping: Record<string, string> = {
        latitude: 'lat',
        longitude: 'lng'
      };

      const queryParams = Object.entries(params).reduce((acc: Record<string, any>, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[mapping[key] || key] = value;
        }
        return acc;
      }, {});

      const response = await this.baseService.http.get('stores/list', { params: queryParams });
      return response.data;
    } catch (error) {
      console.error('Erro stores:', error);
      throw error;
    }
  }

  async cities(state: string): Promise<any> {
    try {
      const response = await this.baseService.http.get(`stores/list/cities?state=${state}`);
      return response.data;
    } catch (error) {
      console.error('Erro cities:', error);
      throw error;
    }
  }

  async neighborhoods(state: string, city: string): Promise<any> {
    try {
      const response = await this.baseService.http.get(`stores/list/neighborhoods?state=${state}&city=${city}`);
      return response.data;
    } catch (error) {
      console.error('Erro neighborhoods:', error);
      throw error;
    }
  }

  async states(): Promise<any> {
    try {
      const response = await this.ApiBaseService.http.get('stores/list/states');
      return response.data;
    } catch (error) {
      console.error('Erro stores:', error);
      throw error;
    }
  }

  async hasStoresInCity(city: string): Promise<any> {
    try {
      const response = await this.ApiBaseService.http.get(`stores/has-stores-in-city?city=${city}`);
      return response.data;
    } catch (error) {
      console.error('Erro hasStoresInCity:', error);
      throw error;
    }
  }

  async banners(params: BannersProps): Promise<BannersProps> {
    try {
      const response = await this.ApiBaseService.http.get<BannersProps>(`banners?store_id=${params.store_id}&order_type=${params.order_type}`);
      return response.data;
    } catch (error) {
      console.error('Erro banners:', error);
      throw error;
    }
  }

}